/**
 * API client for track rights and credits.
 *
 * Wraps the backend endpoints:
 *   - /api/tracks/:id/rights (CRUD for rights holders)
 *   - /api/tracks/:id/rights/self-owned (mark as self-owned)
 *   - /api/tracks/:id/credits (CRUD for credits)
 *   - /api/organizations (for org autocomplete)
 */
import { apiClient } from "./api-client";
import type { OrganizationType, SellerOrganizationType } from "./organization-types";
import type { WorksheetAssertion } from "./rights-worksheet";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TrackRight {
  id: string;
  track_id: string;
  right_type: "master" | "publishing";
  organization_id: string | null;
  holder_name: string;
  holder_email: string | null;
  /**
   * The registry identity this right actually attaches to. `holder_name` is NOT a substitute: two
   * live parties can share a normalized name, and in production a right whose holder_name reads
   * "SONY/ATV TUNES LLC" points at the party "SONY ATV TUNES LLC" — a different row. The holder
   * picker needs this to reopen an existing right on the identity it really has.
   */
  holder_party_id: string | null;
  /** Embedded party (no contact details — those are not in the rights response). */
  parties?: {
    id: string;
    display_name: string;
    kind: "person" | "organization";
    claimed_by_org_id: string | null;
  } | null;
  contact_name: string | null;
  share_pct: number | null;
  territories: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  status: "confirmed" | "pending" | "disputed" | "expired";
  approval_method: "platform" | "email" | "pre_cleared" | "manual";
  confirmed_at: string | null;
  confirmed_by: string | null;
  last_contacted_at: string | null;
  source: string;
  source_ref: string | null;
  confidence: number | null;
  superseded_by: string | null;
  is_active: boolean;
  notes: string | null;
  metadata: Record<string, any> | null;
  coverage_metadata?: Record<string, any> | null;
  allowed_media_types?: string[] | null;
  allowed_usage_types?: string[] | null;
  allowed_distribution_types?: string[] | null;
  allowed_promotion_types?: string[] | null;
  can_grant_exclusivity?: boolean | null;
  max_license_duration?: string | null;
  max_music_use_duration?: string | null;
  coverage_notes?: string | null;
  coverage_review_status?: "confirmed" | "needs_review" | "uncertain" | string;
  coverage_updated_at?: string | null;
  coverage_updated_by?: string | null;
  created_at: string;
  updated_at: string;
  // Joined org data (from select with organizations)
  organizations?: { id: string; name: string; type: OrganizationType | string } | null;
  /**
   * RESPONSE-ONLY, from POST /api/tracks/{id}/rights and the assertion arm of
   * PATCH /api/track-rights/{id} (DEC-043). True when the RESOLVED holder party is claimed by an org
   * with at least one member: the server asks them in-app (their Rights to Confirm), so the email
   * compose step has nothing to do. Absent when the server could not tell (and on every other
   * route): the caller then keeps the form's own judgement.
   *
   * It exists because the form judges BEFORE the save. A typed holder can resolve server-side (IPI or
   * email rung) onto another org's claimed party, which the form had treated as off-platform.
   */
  holder_on_platform?: boolean;
}

export interface TrackCredit {
  id: string;
  track_id: string;
  name: string;
  role: string;
  share_pct: number | null;
  pro: string | null;
  ipi_number: string | null;
  source: string;
  source_ref: string | null;
  status: "pending" | "confirmed";
  is_active: boolean;
  created_at: string;
}

export interface SourcePartyIdentifier {
  id: string;
  source_party_id: string;
  source_id: string;
  identifier_type: string;
  identifier_value: string;
  normalized_identifier_value: string;
  is_primary: boolean;
}

/**
 * Who OWNS a company, from `corporate_hierarchy`. Curated by staff — NOT registry evidence and
 * NOT a mandate: it says who owns this company, never who they act for on a given work. The two
 * are orthogonal (design §1), which is why they render as separate lines.
 *
 * `null` on the party means NO PARENT ON FILE. It must never be shown as "independent" — we
 * cannot tell those apart, and only 26 corporate groups are on file.
 */
export interface CorporateParent {
  parent_name: string;
  territory: string | null;
  source_label: string;
}

export interface SourceParty {
  id: string;
  source_id: string;
  source_party_key: string;
  display_name: string;
  party_kind: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  country: string | null;
  website: string | null;
  matched_organization_id: string | null;
  match_status: string;
  match_confidence: number | null;
  /** Who owns this company. null = no parent on file, NEVER "independent". */
  corporate_parent?: CorporateParent | null;
  identifiers?: SourcePartyIdentifier[];
}

export interface SourceDataSnapshotSummary {
  id: string;
  source_id: string;
  source_object_type: string;
  source_object_id: string;
  title: string | null;
  artist_name: string | null;
  iswc: string | null;
  isrc: string | null;
  upc: string | null;
  fetched_at: string;
  is_current: boolean;
  created_at: string;
}

export interface SourceRightEntry {
  id: string;
  source_id: string;
  source_data_snapshot_id: string;
  track_id: string | null;
  source_party_id: string;
  source_entry_key: string;
  role: string;
  right_type: string;
  usage_type: string;
  /** Collecting society for this entry (e.g. PRS, MCPS, SACEM). Distinct from source_id. */
  pro: string | null;
  share_percent: number | null;
  share_label: string | null;
  territories: string[] | null;
  source_status: string;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
  snapshot?: SourceDataSnapshotSummary | null;
  source_party?: SourceParty | null;
  /**
   * The CANONICAL party this evidence row is about, projected on read by the
   * `source_right_entries_resolved` view: COALESCE(party_id, source_parties.party_id).
   *
   * `source_party` is a MENTION — one registry's spelling of a company in one file.
   * `resolved_party` is the identity all those mentions were reconciled to, and it is what
   * the modal pre-selects as the holder. Populated on 206,938 of 206,938 evidence rows,
   * so in practice it is always present.
   */
  resolved_party_id?: string | null;
  resolved_party?: ResolvedParty | null;
}

/**
 * The canonical identity behind a source mention. NOT the full `parties` row — the read
 * path takes only what the modal needs, and deliberately omits `contact_email` (OD-19: the
 * address belongs to the identity, and is not re-collected per right).
 */
export interface ResolvedParty {
  id: string;
  display_name: string;
  kind: string;
  /**
   * The org that has CLAIMED this identity, if any. Live: 14 of 34,397 parties. Decides
   * whether the holder can be invited in-app, and whether an administrator edge (which is
   * org->org) can be written at all.
   */
  claimed_by_org_id: string | null;
  /**
   * 'auto' | 'needs_review'. 16,611 of 34,397 parties are needs_review, and 30,157 active
   * import entries resolve to one — so a suggested holder being unreviewed is the COMMON
   * case. The modal marks it rather than withholding the suggestion: the user is
   * confirming a suggestion, not accepting a fact.
   */
  review_status: string | null;
}

export interface SourcePartyRelationship {
  id: string;
  source_id: string;
  source_data_snapshot_id: string;
  source_right_entry_id: string | null;
  track_id: string | null;
  from_source_party_id: string;
  to_source_party_id: string;
  source_relationship_key: string;
  source_relationship_type: string;
  /**
   * How many DISTINCT tracks carry this exact (from, to, type) triple across the catalogue.
   * The only honest answer to "standing arrangement or just this deal" — no registry states
   * contract terms, so recurrence is all we can observe. Suppressed at 1, where it says nothing.
   */
  works_count?: number;
  relationship_context: string;
  relationship_note: string | null;
  source_status: string;
  territories: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
  from_source_party?: SourceParty | null;
  to_source_party?: SourceParty | null;
}

export interface SourceFetchStatus {
  id: string;
  source_id: string;
  track_id: string | null;
  source_object_type: string;
  source_object_id: string | null;
  fetch_key: string;
  fetch_reason: string;
  status: string;
  attempts: number;
  max_attempts: number;
  started_at: string | null;
  completed_at: string | null;
  last_attempt_at: string | null;
  source_data_snapshot_id: string | null;
  error_code: string | null;
  error_message: string | null;
  result_summary: Record<string, any>;
  updated_at: string;
}

export interface TrackSourceRightsResponse {
  items: SourceRightEntry[];
  relationships: SourcePartyRelationship[];
  fetch_status: SourceFetchStatus | null;
  total: number;
}

/** Evidence from a single source (MLC, PRS, SACEM, …) for one track. */
export interface SourceEvidenceBlock {
  source_id: string;
  display_name: string;
  source_type: string | null;
  items: SourceRightEntry[];
  relationships: SourcePartyRelationship[];
  fetch_status: SourceFetchStatus | null;
}

/** All-sources response for one track (source_id omitted). */
export interface TrackSourceRightsGroupedResponse {
  sources: SourceEvidenceBlock[];
  total: number;
}

export interface TrackRightRelationship {
  id: string;
  track_id: string;
  right_type: "master" | "publishing" | "neighboring" | "all" | "unknown";
  relationship_type:
    | "administrator_for"
    | "subpublisher_for"
    | "collects_for"
    | "distributor_for"
    | "agent_for"
    | "other"
    | string;
  from_organization_id: string;
  to_organization_id: string;
  from_track_right_id: string | null;
  to_track_right_id: string | null;
  territories: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  status: "active" | "inactive" | "disputed" | "superseded" | string;
  source: string;
  source_party_relationship_id: string | null;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  from_organization?: Organization | null;
  to_organization?: Organization | null;
  from_track_right?: TrackRight | null;
  to_track_right?: TrackRight | null;
  source_party_relationship?: {
    id: string;
    source_id: string;
    source_relationship_type: string;
    relationship_context: string;
    relationship_note: string | null;
    source_status: string;
  } | null;
}

export interface Organization {
  id: string;
  name: string;
  type: string;
  country: string | null;
  logo_url?: string | null;
  contact_email: string | null;
  contact_phone?: string | null;
  alt_email?: string | null;
  alt_phone?: string | null;
  website?: string | null;
  address?: string | null;
  tax_id?: string | null;
  ipi_number?: string | null;
  parent_org_id?: string | null;
}

export interface OrganizationPlatformUser {
  user_id: string;
  display_name: string | null;
  email: string | null;
  role: string | null;
  org_role: string | null;
}

export interface RightsHolderInviteDetails {
  invite: {
    id: string;
    status: "pending" | "accepted" | "expired" | "revoked" | "failed";
    /**
     * Invite origin (D7.4). `confirm_right` carries a seeded right to confirm; `license_request`
     * (catalog "Request License" / off-platform "Request a Song") carries no rights side —
     * `right` is null and the recipient declares their role on the request after accepting.
     */
    purpose: "confirm_right" | "license_request";
    recipient_email: string;
    expires_at: string;
    accepted_at: string | null;
    /**
     * Must the holder STATE their grant, or may the accept inherit it?
     *
     * True for an EVIDENCE-anchored invite — one raised from a registry listing. A registry's
     * share is often absent entirely and its territories are registry-scoped ({US} MLC, {DE}
     * GEMA), so inheriting would put a number in confirmed canon, under the holder's name, that
     * the holder never stated. The server refuses such an accept without share + territories;
     * this flag is what lets the screen ask first instead of discovering it as a 400.
     *
     * False for a declaration-anchored invite: its anchor is a first party's own statement.
     */
    requires_holder_terms?: boolean;
    /** Which rung of the address ladder found this recipient. Provenance, not display. */
    recipient_email_source?: string | null;
  };
  track: { id: string; title: string };
  /**
   * The holder's platform ACCOUNT as the INVITE names it — never the accepter's (that is
   * `accepted_organization`). Null whenever the invited party is unclaimed: no organization holds
   * that identity yet. Today that is the declaration door (which the license request page's
   * "Save & invite" uses; Drafts never emails — DEC-043) inviting an unclaimed party. Who the
   * invite is about is then `right.holder_name`; an accepter with no org creates their own with the
   * accept (`new_organization`), and the invited party is claimed for it.
   * Once accepted, such an invite names the org that holds the party (usually the accepter's).
   */
  organization: {
    id: string;
    name: string;
    type?: string | null;
    country?: string | null;
    logo_url?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    website?: string | null;
    address?: string | null;
    ipi_number?: string | null;
  } | null;
  right: {
    id: string;
    right_type: "master" | "publishing";
    holder_name: string;
    territories: string[] | null;
    share_pct: number | null;
    status: string;
  } | null;
  negotiation: {
    deal_id: string;
    clearance_unit_id: string;
    request_id: string;
    request_track_id: string;
  } | null;
  request_target: {
    request_id: string;
    request_track_id: string;
  } | null;
  /**
   * The ACCEPTER's organization after an accept: the one they created with it, the invite's org
   * they were linked to, or the one they already had. Null when they still have none. Answered by
   * the accept endpoint; absent elsewhere. `org_role` is the accepter's position in it: owner when
   * they created it or were the first into an org nobody else belongs to, else what they hold
   * (DEC-061). Absent from an older server, which gates the completion step shut.
   */
  accepted_organization?: { id: string; name: string; org_role?: string | null } | null;
}

export interface SendRightsHolderInviteResult {
  invite: RightsHolderInviteDetails["invite"];
  email_sent: boolean;
  invite_url: string;
  last_error: string | null;
}

/**
 * Where an accepted rights-holder invite should land the recipient. An active negotiation (or a
 * request-anchored `license_request` invite) routes to the per-request deal console, scoped with
 * `?track=<request_track_id>` to the track the invite was about — the console then shows that
 * track's deal and offers Start / declare only for that track (LicenseRequestDealConsolePage →
 * LiveDealConsole `scopedRequestTrackId`). Without the scope, a recipient holding sides on several
 * tracks was offered — and Start opened — the FIRST one in request order, whichever track that was.
 * Otherwise the request overview; a `confirm_right` with no request goes to the holder's confirmed
 * rights when they are listed for the accepter's org (`isInviteListedForOrg`); else the invite page.
 * Shared by OnboardingPage and RightsHolderInvitePage so post-accept routing stays consistent.
 */
export function getInviteDestination(
  details: RightsHolderInviteDetails | null,
  token: string,
): string {
  const consoleFor = (requestId: string, requestTrackId: string | null | undefined): string => {
    const trackId = String(requestTrackId ?? "").trim();
    const base = `/licenses/${requestId}/console`;
    return trackId ? `${base}?track=${encodeURIComponent(trackId)}` : base;
  };
  const negotiation = details?.negotiation;
  if (negotiation?.request_id) {
    return consoleFor(negotiation.request_id, negotiation.request_track_id);
  }
  const requestTarget = details?.request_target;
  if (requestTarget?.request_id) {
    if (requestTarget.request_track_id) {
      return consoleFor(requestTarget.request_id, requestTarget.request_track_id);
    }
    return `/licenses/${requestTarget.request_id}`;
  }
  // No request behind a confirm_right invite (one sent from a track's rights): the holder goes to the
  // rights they have confirmed, not back to the invite they just answered — when those are listed for
  // their org. Another org already held the party → the invite page, which says what happened.
  if (
    details?.invite?.purpose === "confirm_right" &&
    isInviteListedForOrg(details, details.accepted_organization?.id)
  ) {
    return "/rights-to-confirm?tab=accepted";
  }
  return `/rights/invite/${token}`;
}

/**
 * Does Rights to Confirm list this invite for `orgId`? It lists invites by the org they name
 * (`organization`), which for an invite that named none is the org holding the invited party once
 * accepted — not necessarily the accepter's.
 */
export function isInviteListedForOrg(
  details: RightsHolderInviteDetails | null,
  orgId: string | null | undefined,
): boolean {
  const inviteOrgId = details?.organization?.id;
  return Boolean(inviteOrgId) && inviteOrgId === orgId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rights API
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchTrackRights(
  trackId: string,
  options: boolean | { includeInactive?: boolean; requestTrackId?: string } = false,
): Promise<TrackRight[]> {
  const includeInactive = typeof options === "boolean" ? options : options.includeInactive ?? false;
  const requestTrackId = typeof options === "boolean" ? undefined : options.requestTrackId;
  const qp = new URLSearchParams();
  if (includeInactive) qp.set("include_inactive", "true");
  if (requestTrackId) qp.set("request_track_id", requestTrackId);
  const params = qp.toString();
  const result = await apiClient.get<{ items: TrackRight[]; total: number }>(
    `/api/tracks/${trackId}/rights${params ? `?${params}` : ""}`
  );
  return result.items;
}

/**
 * Fetch source rights evidence for a track across ALL active sources,
 * grouped by source (MLC, PRS, SACEM, …). Used by the track-detail Rights tab.
 *
 * (T14 dropped the single-source `fetchTrackSourceRights` helper — its MLC-only default was the
 * modal's evidence read; the modal now reads grouped evidence across every source.)
 */
export async function fetchTrackSourceRightsGrouped(
  trackId: string,
): Promise<TrackSourceRightsGroupedResponse> {
  return apiClient.get<TrackSourceRightsGroupedResponse>(
    `/api/tracks/${trackId}/source-rights`
  );
}

export async function fetchBatchTrackRights(
  trackIds: string[]
): Promise<Record<string, TrackRight[]>> {
  const result = await apiClient.post<{ rights_by_track: Record<string, TrackRight[]> }>(
    "/api/tracks/batch-rights",
    { track_ids: trackIds }
  );
  return result.rights_by_track;
}

/**
 * Per-track rights indicators (OD-B3) returned alongside batch-rights. Post-P2b a buyer-seeded
 * holder is a `request_declaration` EVIDENCE assertion, not a confirmed `track_rights` row, so the
 * buyer's confirmed-only view carries no holder row for it until the holder accepts. These flags
 * are the buyer's pre-promotion signal that a holder is pending/attributed — surface them so the
 * buyer is not blind in the seed → promotion window.
 */
export interface TrackRightsIndicators {
  has_unpromoted_assertions: boolean;
  has_pending_invite: boolean;
  /**
   * RETIRED 2026-08-10 — replaced by `source_evidence_by_side`, which carries strictly more.
   *
   * The boolean said "a data source stated something about who holds this", and the card rendered
   * that sentence. It is true and unactionable: it names nobody, so there is nothing to click. The
   * rows name each party with its share and role and an entry id, and the entry id is what the
   * evidence-anchored invite door takes. `Object.keys(source_evidence_by_side).length > 0` is the
   * old boolean, exactly, for any caller that only wants existence.
   */
  has_source_attributed_evidence: boolean;
  /**
   * The same population as the boolean above, ENUMERATED per side — added 2026-08-10 for the
   * request card, which now lists each named party with an Invite rather than describing that
   * evidence exists. The boolean STAYS: SearchResultsPage and ProjectWorkspacePage render a chip
   * where existence is the whole message, and handing them rows they would immediately discard is
   * the wrong shape for those surfaces.
   */
  source_evidence_by_side: Record<string, SourceEvidenceSide>;
}

/** One registry statement about one holder on one side — an IMPORT-class `source_right_entries` row. */
export interface SourceEvidenceRow {
  /** The `source_right_entries` id. This is what the evidence-anchored invite door takes. */
  entry_id: string;
  source_id: string | null;
  holder_name: string | null;
  /** Resolved through `source_right_entries_resolved`; an import row carries no `party_id` itself. */
  party_id: string | null;
  source_party_id: string | null;
  role: string | null;
  /** Frequently NULL — 68,737 GEMA+SACEM rows state no share at all. */
  share_pct: number | null;
  territories: string[] | null;
}

/**
 * One side's registry evidence, ordered and capped by the server.
 *
 * `shown` + `hidden_count` ALWAYS equals `total`, and `hidden_share_pct` is the residual rather than
 * a recomputation — which is the only reason a capped list can be trusted. Never re-derive the
 * hidden figures on the client.
 */
export interface SourceEvidenceSide {
  total: number;
  shown: SourceEvidenceRow[];
  hidden_count: number;
  /** NULL, not 0, when nothing hidden states a share — "0%" would read as a stated zero. */
  hidden_share_pct: number | null;
  total_share_pct: number | null;
  sources: string[];
}

/** Batch-rights + the per-track OD-B3 indicators (rights_by_track stays byte-stable). */
export async function fetchBatchTrackRightsWithIndicators(
  trackIds: string[]
): Promise<{
  rightsByTrack: Record<string, TrackRight[]>;
  indicatorsByTrack: Record<string, TrackRightsIndicators>;
}> {
  const result = await apiClient.post<{
    rights_by_track: Record<string, TrackRight[]>;
    indicators_by_track: Record<string, TrackRightsIndicators>;
  }>("/api/tracks/batch-rights", { track_ids: trackIds });
  return {
    rightsByTrack: result.rights_by_track ?? {},
    indicatorsByTrack: result.indicators_by_track ?? {},
  };
}

/**
 * Batch-fetch source rights evidence across ALL active sources, grouped by
 * source per track. Used to prefetch evidence for list pages (e.g. search
 * results) so the track-detail Rights tab can render without a round-trip.
 */
export async function fetchBatchTrackSourceRightsGrouped(
  trackIds: string[]
): Promise<Record<string, SourceEvidenceBlock[]>> {
  const result = await apiClient.post<{
    sources_by_track: Record<string, SourceEvidenceBlock[]>;
  }>("/api/tracks/batch-source-rights", { track_ids: trackIds });
  return result.sources_by_track ?? {};
}


export async function fetchBatchTrackCredits(
  trackIds: string[]
): Promise<Record<string, TrackCredit[]>> {
  const result = await apiClient.post<{ credits_by_track: Record<string, TrackCredit[]> }>(
    "/api/tracks/batch-credits",
    { track_ids: trackIds }
  );
  return result.credits_by_track;
}

export async function addTrackRight(payload: {
  track_id: string;
  right_type: string;
  holder_name: string;
  /**
   * The registry identity the user PICKED. When set the server attaches the right to exactly this
   * party and resolves nothing — necessary because two live parties can share a normalized name, so
   * re-deriving the identity from `holder_name` can land on the other row. Omit for a NEW holder:
   * `holder_name` is then resolved-or-minted as before. (C2 addition, window note 2026-07-27.)
   */
  party_id?: string;
  organization_id?: string;
  holder_email?: string;
  /**
   * IPI for a NEW holder, straight from the create form. It is the STRONGEST rung of the server's
   * resolver ladder — an `ipi:` key matches ahead of email and name — so sending it is what makes
   * a typed IPI actually deduplicate instead of decorate. Ignored by the server when `party_id`
   * is set (the user already picked an identity). Publishing-side only in practice.
   */
  ipi?: string;
  contact_name?: string;
  share_pct?: number;
  /** `null` = the declarer stating the scope is NOT STATED; absent means the same thing on
   *  this CREATE door (there is nothing to inherit) but the explicit null keeps the
   *  statement legible on the wire. `[]` is never sent — it is a slip, not an answer. */
  territories?: string[] | null;
  allowed_media_types?: string[];
  allowed_usage_types?: string[];
  allowed_distribution_types?: string[];
  allowed_promotion_types?: string[];
  can_grant_exclusivity?: boolean;
  max_license_duration?: string;
  max_music_use_duration?: string;
  coverage_notes?: string;
  coverage_review_status?: "confirmed" | "needs_review" | "uncertain" | string;
  status?: string;
  approval_method?: string;
  source?: string;
  notes?: string;
  /**
   * DRAFT PROVENANCE (2026-08-09) — sent ONLY when this holder was drafted from a source
   * evidence row via "use as draft". It is that row's `source_right_entries.id`.
   *
   * Records WHERE THE DRAFT CAME FROM, not that it still matches. The user may edit every
   * copied field and the link stays — the server carries it forward on every superseding
   * append, so the frontend never needs to resend it on an edit, and there is no "edited"
   * flag to keep in sync.
   *
   * ONE id, not two. The snapshot GENERATION is still pinned (snapshots are versioned via
   * is_current / superseded_by, and the generation is what keeps a later diff meaningful
   * across a registry restatement) — but the server derives it from this entry rather than
   * taking it from us. Both halves are cross-tenant pointers the FK cannot scope, and the
   * only snapshot id a client could legitimately send is the one the entry already carries.
   *
   * The server likewise SCOPES this id: kept only when it names an import-class row on the
   * same track, silently dropped otherwise. Provenance never blocks a save.
   *
   * Distinct name from the `source_data_snapshot_id` / `source_party_id` pair that was
   * added and reverted once before: those columns' FKs are composite on `source_id` and
   * cannot reference a registry row from a declaration. See
   * src/test/rights-edit-modal-no-source-ref.test.tsx §R-A6.
   */
  drafted_from_entry_id?: string;
}): Promise<TrackRight> {
  return apiClient.post<TrackRight>(
    `/api/tracks/${payload.track_id}/rights`,
    payload
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// C2 — declaration-assertion writes (the substrate the modal appends to)
// ─────────────────────────────────────────────────────────────────────────────

/** C2 assertion-write body. `source`/`status`/`usage_type` are server-derived (structural deny). */
export interface CreateAssertionPayload {
  side: "master" | "publishing";
  /** party_id XOR new_party. */
  party_id?: string;
  new_party?: {
    display_name: string;
    kind?: "organization" | "person";
    email?: string;
    ipi?: string;
  };
  role?: string;
  share_pct?: number;
  share_label?: string;
  territories?: string[];
  valid_from?: string;
  valid_until?: string;
  /** Stamps the buyer's request_declaration when the caller is a buyer with an active request. */
  request_id?: string;
}

/**
 * Append a declaration-class evidence assertion (C2). Returns the assertion shape
 * (`entry_id`, `party{…}`, `side`, `share_pct`, …) — the same shape the worksheet's
 * `Item.assertion` carries. A stale (non-head) supersede → 409; validation → 400.
 */
export async function createRightsAssertion(
  trackId: string,
  payload: CreateAssertionPayload
): Promise<WorksheetAssertion> {
  return apiClient.post<WorksheetAssertion>(
    `/api/tracks/${trackId}/rights/assertions`,
    payload
  );
}

/** Author-retract a declaration assertion (C2) — the ONE permitted in-place evidence mutation. */
export async function withdrawRightsAssertion(
  trackId: string,
  entryId: string
): Promise<{ entry_id: string; source_status: string }> {
  return apiClient.post<{ entry_id: string; source_status: string }>(
    `/api/tracks/${trackId}/rights/assertions/${entryId}/withdraw`,
    {}
  );
}

/** Record that one company administers another — party to party, no accounts required.
 *
 * Replaces `addTrackRightRelationship` for the administrator field. That endpoint needs an
 * organization on BOTH sides, which is why the field was hidden for 29,535 of 29,549 identities:
 * an unclaimed holder had nothing to link to, so the write was skipped and the seller was still
 * told the right was finalized.
 *
 * Default scope is the subject's whole catalogue — "they administer that catalogue" said once,
 * rather than repeated on every track. This RECORDS the arrangement; it grants nothing until the
 * representation gate reads authorities.
 */
export async function declareAdministration(payload: {
  grantor_party_id: string;
  subject_party_id: string;
  right_type: "master" | "publishing";
  role: string;
  scope_type?: "party_catalog" | "track";
  scope_track_id?: string;
  territories?: string[];
  valid_from?: string;
  valid_until?: string;
  agreement_ref?: string;
}): Promise<{ authority: { id: string } }> {
  return apiClient.post<{ authority: { id: string } }>(
    "/api/rights/administrations",
    payload
  );
}

/** Record that MY organization represents the holder of a given right.

 * The org-shaped door onto `licensing_authorities`, for licensing surfaces that hold organization
 * ids and no parties. Deliberately takes neither party: the grantor is the caller's session org and
 * the subject is read from the right's `holder_party_id`, server-side. `right_type` is taken from
 * the right too, so a master representation cannot be filed against the publishing side.
 *
 * Replaced `addTrackRightRelationship` (2026-08-20) when `track_right_relationships` was retired.
 */
export async function declareAdministrationForRight(payload: {
  track_right_id: string;
  role: string;
  territories?: string[];
  agreement_ref?: string;
}): Promise<{ authority: { id: string } }> {
  return apiClient.post<{ authority: { id: string } }>(
    "/api/rights/administrations/by-right",
    payload
  );
}


export async function updateTrackRightRelationship(
  relationshipId: string,
  updates: Partial<TrackRightRelationship>
): Promise<TrackRightRelationship> {
  return apiClient.patch<TrackRightRelationship>(
    `/api/track-right-relationships/${relationshipId}`,
    updates
  );
}

export async function removeTrackRightRelationship(relationshipId: string): Promise<void> {
  await apiClient.delete(`/api/track-right-relationships/${relationshipId}`);
}

/**
 * PATCH body for `updateTrackRight`. Mirrors backend `UpdateTrackRightInput`.
 *
 * Was `Partial<TrackRight>` — the ROW shape, which has no `party_id`. The modal sent one anyway
 * (a variable, so no excess-property check), Pydantic's `extra='ignore'` dropped it, and the holder
 * change vanished with the toast still reporting success. Typing the WIRE shape here is what makes
 * a field the server doesn't accept a compile error instead of a silent no-op.
 */
export interface UpdateTrackRightInput extends Partial<TrackRight> {
  /**
   * Move the right to a different party (window note 2026-07-28). Unconfirmed → C2 withdraw + new-key
   * append; confirmed → 409-equivalent refusal, since the confirmation belongs to the old party.
   * Only an id counts — `holder_name` is display-only on this route.
   */
  party_id?: string;
  /**
   * Move the right to a holder that is NOT yet in the registry (window note 2026-07-30). The picker's
   * create-new flow yields a holder with no id, and this route ignores `holder_name` by design, so
   * without this the change was silently discarded while the toast reported success. Mutually
   * exclusive with `party_id` — sending both is refused rather than resolved by preference.
   */
  new_party?: {
    display_name: string;
    kind: string;
    email?: string;
    ipi?: string;
  };
}

export async function updateTrackRight(
  rightId: string,
  updates: UpdateTrackRightInput
): Promise<TrackRight> {
  return apiClient.patch<TrackRight>(
    `/api/track-rights/${rightId}`,
    updates
  );
}

export async function removeTrackRight(rightId: string): Promise<void> {
  await apiClient.delete(`/api/track-rights/${rightId}`);
}

/** C3 per-side finalize body: confirm one side's declaration assertion into canon. */
export interface FinalizeSidePayload {
  /** The declaration-class source_right_entries id to promote. */
  assertion_entry_id: string;
  confirmed_basis?: "party_self" | "licensor_agreement" | "staff_review";
  /** Required iff confirmed_basis === 'licensor_agreement'. */
  confirmed_via_authority_id?: string;
}

/**
 * Finalize v2 (C3): promote the chosen per-side declaration assertion(s) to confirmed canon. The
 * legacy flat `master_right_id`/`publishing_right_id` params are GONE (T14 FE flip) — send the
 * per-side body. `holder_party_id` is derived server-side; never caller-supplied. A stale (non-head)
 * assertion → 409 (parse via `parseStaleAssertionError`); withdrawn/disputed/side-mismatch → 400.
 */
export async function finalizeTrackRights(
  trackId: string,
  payload: {
    master?: FinalizeSidePayload;
    publishing?: FinalizeSidePayload;
  }
): Promise<{
  master: TrackRight | null;
  publishing: TrackRight | null;
  rights_status: string;
}> {
  return apiClient.post(`/api/tracks/${trackId}/rights/finalize`, payload);
}

/**
 * Send an EMAIL invite for an off-platform rights holder (T14). `rightId` is the SRE declaration
 * assertion id (the invite anchors it). `organization_id` is the modal's selected off-platform org —
 * the invitee links to it on signup and the accept claims its party.
 */
export async function sendRightsHolderInvite(
  rightId: string,
  payload: {
    organization_id?: string;
    /**
     * The LAST rung of the §2.4 address ladder — an address the seller typed because nothing the
     * party, a registry or the org published answered. Sending it cannot override a published
     * address; the ladder tries it last. Recorded on the invite as `typed_by_seller`, and never
     * written to the party.
     */
     recipient_email?: string;
    /** The seller's optional message. Reaches the email body (backend, 2026-08-26). */
    note?: string;
  } = {}
): Promise<SendRightsHolderInviteResult> {
  return apiClient.post<SendRightsHolderInviteResult>(
    `/api/track-rights/${rightId}/invite`,
    payload
  );
}

/**
 * What the invite will say, and who it will go to. Sends nothing and writes nothing.
 *
 * Runs the SAME resolution and the SAME guardrails as the send, so a refusal — the position is
 * disputed, the org already has platform users, no address on file — arrives while the compose
 * panel is still open. The one difference from the sent mail is the link: the preview carries a
 * placeholder, because minting a token to be looked at would create a live credential nobody
 * received and revoke a pending invite that may already be out.
 *
 * `no_address_on_file` comes back in the same shape the send uses, which is what lets one inline
 * address field serve both steps.
 */
export interface RightsHolderInvitePreview {
  recipient_email: string;
  /** Which rung answered: party | mention_same_source | mention_cross_source | organization | typed_by_seller */
  recipient_email_source: string;
  recipient_name: string;
  track_title: string;
  right_type: string;
  subject: string;
  text: string;
}

export async function previewRightsHolderInvite(
  rightId: string,
  payload: { organization_id?: string; recipient_email?: string; note?: string } = {}
): Promise<RightsHolderInvitePreview> {
  return apiClient.post<RightsHolderInvitePreview>(
    `/api/track-rights/${rightId}/invite/preview`,
    payload
  );
}

/**
 * The `no_address_on_file` state, unwrapped from an ApiError.
 *
 * NOT `err.detail`. `ApiError` puts the parsed response body on `.body`, so the two existing
 * handlers reading `(err as {detail?}).detail` have never once matched — which is why the inline
 * address field they gate has never appeared. The value lives at `err.body.detail.reason`.
 */
export function parseNoAddressOnFile(err: unknown): {
  party: { id: string | null; display_name: string | null };
  known_contact: { address: string | null; country: string | null; phone: string | null };
} | null {
  const detail = (err as { body?: { detail?: unknown } })?.body?.detail as
    | { reason?: string; party?: unknown; known_contact?: unknown }
    | undefined;
  if (!detail || detail.reason !== "no_address_on_file") return null;
  return {
    party: (detail.party as { id: string | null; display_name: string | null }) ?? {
      id: null,
      display_name: null,
    },
    known_contact: (detail.known_contact as {
      address: string | null;
      country: string | null;
      phone: string | null;
    }) ?? { address: null, country: null, phone: null },
  };
}

export async function fetchRightsHolderInvite(
  token: string
): Promise<RightsHolderInviteDetails> {
  return apiClient.get<RightsHolderInviteDetails>(
    `/api/rights-holder-invites/${token}`
  );
}

/**
 * The holder's own statement of what they grant. Sent only when the invite asks for it
 * (`requires_holder_terms`); a declaration-anchored accept still sends nothing and inherits.
 */
export interface HolderTerms {
  share_pct: number;
  territories: string[];
}

/**
 * Invite the holder an IMPORT evidence row NAMES — the evidence-anchored door (join §2).
 *
 * Distinct from `sendRightsHolderInvite`, which anchors a DECLARATION and needs the holder's org
 * to exist. This one needs no org at all: that is the point, because a registry names identities,
 * not organisations.
 *
 * `recipientEmail` is RUNG 4 of the address ladder — supply it only after the server has told you
 * no address is on file. Sending it does not override a published address; the server tries it last.
 *
 * Throws with `reason: "no_address_on_file"` and a `known_contact` block when every rung is empty.
 * That is a STATE to render (ask for an address), not a failure to report.
 */
export interface EvidenceInviteResult {
  invite: { id: string; status: string; recipient_email: string };
  email_sent: boolean;
  recipient_email_source: string;
  last_error?: string | null;
}

export async function inviteHolderFromEvidence(
  trackId: string,
  entryId: string,
  options?: { recipientEmail?: string; note?: string }
): Promise<EvidenceInviteResult> {
  return apiClient.post<EvidenceInviteResult>(
    `/api/tracks/${trackId}/rights/evidence/${entryId}/invite`,
    {
      ...(options?.recipientEmail ? { recipient_email: options.recipientEmail } : {}),
      ...(options?.note ? { note: options.note } : {}),
    }
  );
}

/**
 * The invitee's OWN new organization, created by the accept itself (`new_organization`).
 *
 * Sent only by an accepter who has no organization, for an invite that names none: the server
 * creates this org (the accepter becomes its owner) and the invited party is claimed for it.
 * Seller types only. The same details org creation takes, minus the parent link (the invitee's org
 * is always their own, top-level). `country` is the comma-joined ISO string onboarding's org form
 * produces.
 */
export interface InviteNewOrganization {
  name: string;
  type: SellerOrganizationType;
  country: string;
  website?: string | null;
  address?: string | null;
  tax_id?: string | null;
  ipi_number?: string | null;
  logo_url?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  alt_email?: string | null;
  alt_phone?: string | null;
}

export async function acceptRightsHolderInvite(
  token: string,
  terms?: HolderTerms,
  newOrganization?: InviteNewOrganization
): Promise<RightsHolderInviteDetails> {
  const accepted = await apiClient.post<RightsHolderInviteDetails>(
    `/api/rights-holder-invites/${token}/accept`,
    {
      ...(terms ? { share_pct: terms.share_pct, territories: terms.territories } : {}),
      ...(newOrganization ? { new_organization: newOrganization } : {}),
    }
  );
  // An accept can link the account to an organization and make it active: what lives for the
  // whole session (the player's choice of audio, for one) reads the profile again.
  window.dispatchEvent(new Event("profileUpdated"));
  return accepted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Holder-confirm gate (in-app)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One pending in-app holder-confirm invite the viewer must act on, for a given license request.
 * A buyer seeded the viewer's org as a rights holder; the org confirms or rejects the tag.
 */
export interface PendingHolderInvite {
  invite_id: string;
  track_id: string;
  track_title: string;
  right_type: "master" | "publishing" | string | null;
  holder_name: string | null;
  share_pct: number | null;
  territories: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  invited_by_name: string;
  /** The ORGANIZATION that recorded it. A person's name means nothing to an outside holder. */
  invited_by_org_name?: string | null;
  /** `confirm_right` = recorded against their catalog · `license_request` = a live deal. */
  context?: "confirm_right" | "license_request" | string;
  request_id?: string | null;
  created_at?: string | null;
  /** `pending` · `accepted` · `declined`, or `expired` for a pending row past its date. */
  status?: "pending" | "accepted" | "declined" | "expired" | string;
  responded_at?: string | null;
  /** Enough of the track to RECOGNISE it — a bare title is not enough to judge a claim by. */
  artist?: string | null;
  album?: string | null;
  artwork_url?: string | null;
}

/** Fetch the viewer's pending holder-confirm invites for a request (empty for buyers/coordinators). */
export async function fetchMyPendingHolderInvites(
  requestId: string
): Promise<PendingHolderInvite[]> {
  const result = await apiClient.get<{ items: PendingHolderInvite[] }>(
    `/api/license-requests/${requestId}/my-pending-holder-invites`
  );
  return result.items ?? [];
}

/**
 * Everything waiting on the caller's ORG to confirm — the "Rights to Confirm" page.
 *
 * Catalog-wide, unlike the request-scoped read above: a `confirm_right` invite raised from Drafts
 * has no request to scope by, and that is precisely the case that previously had no surface at all.
 * An on-platform holder is never emailed and the raw invite token is discarded after creation, so
 * before this endpoint their invite was unanswerable by anyone. See docs/rights-flow.md §3.4b.
 */
export async function fetchMyRightsToConfirm(): Promise<PendingHolderInvite[]> {
  const result = await apiClient.get<{ items: PendingHolderInvite[]; total: number }>(
    "/api/rights-invites/mine"
  );
  return result.items ?? [];
}

/** Confirm ('accept') or reject ('decline') a buyer-seeded holder tag. */
export async function respondToHolderInvite(
  inviteId: string,
  action: "accept" | "decline",
  reason?: string,
  /**
   * The holder's own values, when they are the right party but the terms are wrong. Recorded as a
   * HOLDER-AUTHORED superseding assertion (attributed to them, not the seller who guessed) and
   * promoted, so the confirmed right carries what they said. Accept-only; send only what changed.
   */
  /** `territories: null` = the holder states "scope not stated yet"; an absent key inherits the
   *  anchor's value; `[]` is refused by the door. Three states, deliberately. */
  correction?: {
    share_pct?: number;
    territories?: string[] | null;
    valid_from?: string;
    valid_until?: string;
  }
): Promise<{ invite_id: string; action: string; status: string }> {
  return apiClient.post(`/api/rights-invites/${inviteId}/respond`, {
    action,
    ...(reason ? { reason } : {}),
    ...(correction && Object.keys(correction).length ? { correction } : {}),
  });
}

/**
 * One of the caller org's own claimed identities — an ARM.
 *
 * An org is not one identity: `parties.claimed_by_org_id` carries no unique index, so Believe
 * holds BELIEVE DIRECT LTD, BELIEVE INTERNATIONAL and believeforzen at once. Ordered
 * oldest-claim-first by the API, matching `get_party_claimed_by_org`'s disambiguation rule, so
 * `items[0]` is the identity the rest of the system already treats as this org's default.
 */
export interface MyOrgParty {
  id: string;
  display_name: string | null;
  kind: string | null;
  country: string | null;
  contact_email: string | null;
  claimed_at: string | null;
  claimed_via: string | null;
}

/**
 * The caller org's claimed identities, for the per-side arm picker.
 *
 * Takes no org argument ON PURPOSE — the server reads it from the authenticated profile, so this
 * cannot be pointed at another tenant. Returns [] for a user with no org rather than erroring:
 * no org means no arms to choose between, and the picker falls back to name resolution.
 */
export async function fetchMyOrgParties(): Promise<MyOrgParty[]> {
  // `/claimed/mine`, NOT `/mine` — that path already belongs to the single-identity lookup behind
  // "I am the rights holder", and registering this one on it shadowed that route server-side.
  const res = await apiClient.get<{ items: MyOrgParty[] }>(`/api/parties/claimed/mine`);
  return res?.items ?? [];
}

export async function markSelfOwned(payload: {
  track_id: string;
  /**
   * Which side to declare. Sending it RETIRES the transition shim: the backend covered BOTH
   * sides when this was omitted, purely because this call site did not send one
   * (`mark_self_owned` B0.1 docstring). Every caller now sends a side.
   */
  right_type?: "master" | "publishing";
  // No `organization_id` and no `holder_name`: the holder is ALWAYS the caller's own organization,
  // under the org's own identity (its oldest claim, else the org's name). The server refuses a
  // different org and refuses a typed holder name out loud (tasks/48 §S8).
  /** Which ARM of your org holds this side. Must already be claimed by your org. */
  holder_party_id?: string;
  /**
   * The COVERAGE the declaration claims. Both are optional at the API — B0.1 made them close-gate
   * facts rather than start-gate ones, so a declaration with neither is still a valid statement of
   * "this is mine". DEC-030's point is that the operator CONFIRMS rather than a click silently
   * writing 100%/worldwide — so the territory is no longer pre-ticked Worldwide (2026-09-21); the
   * operator states it, chooses Worldwide, or says the scope is not stated.
   *
   * `territories: null` is that last answer. An absent key means the same thing on this door
   * because it is a CREATE — there is nothing to inherit — but the explicit null is what the
   * caller sends, so the statement is legible on the wire.
   */
  share_pct?: number;
  territories?: string[] | null;
}): Promise<TrackRight[]> {
  return apiClient.post<TrackRight[]>(
    `/api/tracks/${payload.track_id}/rights/self-owned`,
    payload
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Credits API
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchTrackCredits(
  trackId: string,
  includeInactive = false,
  roleFilter?: string
): Promise<TrackCredit[]> {
  const qp = new URLSearchParams();
  if (includeInactive) qp.set("include_inactive", "true");
  if (roleFilter) qp.set("role", roleFilter);
  const qs = qp.toString();
  const result = await apiClient.get<{ items: TrackCredit[]; total: number }>(
    `/api/tracks/${trackId}/credits${qs ? `?${qs}` : ""}`
  );
  return result.items;
}

export async function addTrackCredit(payload: {
  track_id: string;
  name: string;
  role: string;
  share_pct?: number;
  pro?: string;
  ipi_number?: string;
  source?: string;
  status?: "pending" | "confirmed";
}): Promise<TrackCredit> {
  return apiClient.post<TrackCredit>(
    `/api/tracks/${payload.track_id}/credits`,
    payload
  );
}

export async function updateTrackCredit(
  creditId: string,
  updates: Partial<TrackCredit>
): Promise<TrackCredit> {
  return apiClient.patch<TrackCredit>(
    `/api/track-credits/${creditId}`,
    updates
  );
}

export async function removeTrackCredit(creditId: string): Promise<void> {
  await apiClient.delete(`/api/track-credits/${creditId}`);
}

export async function bulkAddTrackCredits(payload: {
  track_id: string;
  credits: { name: string; role: string; share_pct?: number; pro?: string }[];
}): Promise<TrackCredit[]> {
  const result = await apiClient.post<{ credits: TrackCredit[]; count: number }>(
    `/api/tracks/${payload.track_id}/credits/bulk`,
    payload
  );
  return result.credits;
}

// ─────────────────────────────────────────────────────────────────────────────
// MLC Enrichment API
// ─────────────────────────────────────────────────────────────────────────────

export interface MLCEnrichmentResult {
  status: "enriched" | "not_found" | "partial";
  message?: string;
  track_id: string;
  isrc: string;
  mlc_song_code?: string;
  iswc?: string | null;
  title?: string;
  writers_added?: number;
  publishers_added?: number;
  total_collection_share?: number;
  organizations_linked?: number;
}

export async function enrichTrackFromMLC(
  trackId: string
): Promise<MLCEnrichmentResult> {
  return apiClient.post<MLCEnrichmentResult>(
    `/api/tracks/${trackId}/enrich-mlc`,
    {}
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Organizations API (for autocomplete)
// ─────────────────────────────────────────────────────────────────────────────

export async function searchOrganizations(
  search: string,
  limit = 10
): Promise<Organization[]> {
  const result = await apiClient.get<{ items: Organization[] }>(
    `/api/organizations?search=${encodeURIComponent(search)}&limit=${limit}`
  );
  return result.items;
}

export interface CreateOrganizationPayload {
  name: string;
  type: OrganizationType;
  country: string;
  logo_url?: string;
  contact_email?: string;
  contact_phone?: string;
  alt_email?: string;
  alt_phone?: string;
  website?: string;
  address?: string;
  tax_id?: string;
  ipi_number?: string;
  parent_org_id?: string | null;
  auto_link_creator?: boolean;
}

export async function createOrganization(payload: CreateOrganizationPayload): Promise<Organization> {
  return apiClient.post<Organization>(`/api/organizations`, payload);
}

export interface OrganizationPlatformUsers {
  /** The people, for members of that organization; empty for everyone else (`members_visible`). */
  items: OrganizationPlatformUser[];
  /** How many there are (up to `limit`): whether the org is on the platform reads from this, and
   *  it is the only thing a non-member learns. */
  total: number;
  has_more: boolean;
  /** False for a caller outside the organization: `items` is then empty while `total` counts.
   *  Absent from a backend older than that rule; read the count either way. */
  members_visible?: boolean;
}

export async function fetchOrganizationPlatformUsers(
  organizationId: string,
  limit = 50
): Promise<OrganizationPlatformUsers> {
  return apiClient.get<OrganizationPlatformUsers>(
    `/api/organizations/${organizationId}/platform-users?limit=${limit}`
  );
}
